import { NetworkTreeDto } from '../../interfaces/IAffectedCustomers'

/**
 * Whether a single network-tree node matches a search term.
 * Used both by react-arborist's tree filter and by NetworkTreeBox's
 * visibleNodesUnderSearch computation so the two stay in sync.
 */
export const matchesNetworkTreeNode = (node: NetworkTreeDto, term: string): boolean => {
  if (!term) return false
  const lower = term.toLowerCase()
  return (
    !!node.name?.toLowerCase().includes(lower) ||
    !!node.managementIp?.toLowerCase().includes(lower) ||
    !!node.servicePointReference?.toLowerCase().includes(lower) ||
    !!node.circuitNumbers?.some((c) => c.toLowerCase().includes(lower))
  )
}

/**
 * Display categories for network node search hits. The search endpoint returns
 * every node type in one response, so the categories are what tells the user
 * whether a hit is a technical service or a piece of equipment.
 */
export const NETWORK_NODE_CATEGORIES = {
  service: 'Tjenester',
  servicePoint: 'Leveransepunkt',
  equipment: 'Utstyr',
  networkInterface: 'Grensesnitt',
  other: 'Annet',
} as const

// Keyed lowercase: the backend compares node types case-insensitively
// (AffectedNetworkNodeNameKind.FromNodeType), so we do the same.
const CATEGORY_BY_NODE_TYPE = new Map<string, string>([
  ['technicalservice', NETWORK_NODE_CATEGORIES.service],
  ['technicalserviceoption', NETWORK_NODE_CATEGORIES.service],
  ['servicepoint', NETWORK_NODE_CATEGORIES.servicePoint],
  ['equipment', NETWORK_NODE_CATEGORIES.equipment],
  ['interface', NETWORK_NODE_CATEGORIES.networkInterface],
])

export const networkNodeCategory = (nodeType: string | null | undefined): string =>
  CATEGORY_BY_NODE_TYPE.get((nodeType ?? '').trim().toLowerCase()) ?? NETWORK_NODE_CATEGORIES.other

/**
 * Orders search hits so every category forms one contiguous block, which is what
 * MUI's Autocomplete needs to render a single header per group.
 *
 * The incoming order is the backend's relevance order, so groups are ordered by
 * their best-ranked hit: search for a service name and "Tjenester" comes first,
 * search for equipment and "Utstyr" comes first. Sort is stable, so the relevance
 * order is preserved inside each group.
 */
export const groupOptionsByCategory = <T extends { category?: string }>(options: T[]): T[] => {
  const firstSeenAt = new Map<string, number>()

  options.forEach((option, index) => {
    const category = option.category ?? ''
    if (!firstSeenAt.has(category)) firstSeenAt.set(category, index)
  })

  return [...options].sort((a, b) => (firstSeenAt.get(a.category ?? '') ?? 0) - (firstSeenAt.get(b.category ?? '') ?? 0))
}
